/**
 * AssistanceMintForm — the assistance-template mint form the Apple and Google
 * studios share (lifted from the unrouted PassStudio's AssistanceStep2Form so
 * the routed studios don't grow a drifting re-implementation). Recipient +
 * assistance service fields; NO flight lookup — the assistance template has no
 * flight, so the submit is gated only on the in-flight mint, never on
 * `flightLookup.flight`. Schemas + servicePayload builder are the SHARED
 * passForms/mintPayloads fragments.
 *
 * The platform envelope (offeredPlatforms + appleStyle/googleStyle) comes from
 * the host so this form stays platform-agnostic; the host owns the `mint`
 * mutation and renders the error + result UI around it.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import type { UseMutationResult } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { useT, type Translate } from '../../i18n';
import { ymdLocalPlusDays } from '../../lib/localDate';
import type { DemoPass, MintPassRequest } from '../../types';
import { AssistanceDataFields } from '../passForms/AssistanceDataFields';
import { RecipientFields } from '../passForms/RecipientFields';
import {
  assistanceContentSchemaFields,
  buildAssistanceServicePayload,
  recipientSchemaFields,
} from '../passForms/mintPayloads';

// Rebuilt per language (useMemo on `t`) so zod messages follow the HU/EN
// switch — see TravelMintForm for the resolver-freshness rationale.
const buildAssistanceSchema = (t: Translate) =>
  z.object({
    ...recipientSchemaFields(t),
    ...assistanceContentSchemaFields(t),
  });
type AssistanceValues = z.infer<ReturnType<typeof buildAssistanceSchema>>;

/** The studios' shared mint-mutation shape (host-owned). */
export type MintMutation = UseMutationResult<DemoPass, Error, MintPassRequest>;

export interface AssistanceMintFormProps {
  defaultEmail: string;
  mint: MintMutation;
  /**
   * The platform-specific mint envelope — exactly one style key + its
   * offeredPlatforms, spread verbatim into the request (so the Apple host
   * never leaks a googleStyle key and vice versa).
   */
  platformFields: Pick<MintPassRequest, 'offeredPlatforms' | 'appleStyle' | 'googleStyle'>;
}

export function AssistanceMintForm({
  defaultEmail,
  mint,
  platformFields,
}: AssistanceMintFormProps): JSX.Element {
  const t = useT();
  const assistanceSchema = useMemo(() => buildAssistanceSchema(t), [t]);
  const today = ymdLocalPlusDays(0);
  const oneYear = ymdLocalPlusDays(365);
  const { register, handleSubmit, formState } = useForm<AssistanceValues>({
    resolver: zodResolver(assistanceSchema),
    defaultValues: {
      recipientName: '',
      recipientEmail: defaultEmail,
      // The service NAME titles the minted pass (design title + description) —
      // the default matches the sample seed's general-insurance copy.
      loungeName: '24/7 Általános Asszisztencia',
      loungeCode: 'IW',
      accessFrom: today,
      accessTo: oneYear,
      assistancePhone: '',
      policyNumber: '',
      esimLinkUrl: '',
    },
  });

  const onSubmit = (v: AssistanceValues): void => {
    mint.mutate({
      recipientName: v.recipientName,
      recipientEmail: v.recipientEmail,
      templateKind: 'assistance',
      servicePayload: buildAssistanceServicePayload(v),
      ...platformFields,
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <RecipientFields register={register} errors={formState.errors} nameVariant="customer" />
      <AssistanceDataFields register={register} errors={formState.errors} />
      <button type="submit" disabled={mint.isPending} className="btn-primary w-full">
        {mint.isPending ? t('form.minting') : t('form.mint')}
      </button>
    </form>
  );
}
